/*******************************************************************************
 *
 * Turbo Run CLI Enums
 *
 * `as const` enums for the enumerated value flags on the `turbo run` CLI
 * surface (Turbo 2.9.18). Mirrors the pattern used by the `cdk` CLI enums in
 * `../aws/cdk-cli-enums.ts`: a named `as const` object per flag plus a derived
 * union type, so consumers get autocomplete and the compiler rejects typos
 * without us reaching for a TypeScript `enum`.
 *
 * Consumed by {@link TurboRunOptions} in `./turbo-repo`.
 *
 ******************************************************************************/

/**
 * `--continue` values accepted by `turbo run`.
 */
export const TURBO_RUN_CONTINUE = {
  Never: "never",
  DependenciesSuccessful: "dependencies-successful",
  Always: "always",
} as const;

/**
 * Type for {@link TURBO_RUN_CONTINUE} values.
 */
export type TurboRunContinue =
  (typeof TURBO_RUN_CONTINUE)[keyof typeof TURBO_RUN_CONTINUE];

/**
 * `--output-logs` values accepted by `turbo run`.
 */
export const TURBO_RUN_OUTPUT_LOGS = {
  Full: "full",
  None: "none",
  HashOnly: "hash-only",
  NewOnly: "new-only",
  ErrorsOnly: "errors-only",
} as const;

/**
 * Type for {@link TURBO_RUN_OUTPUT_LOGS} values.
 */
export type TurboRunOutputLogs =
  (typeof TURBO_RUN_OUTPUT_LOGS)[keyof typeof TURBO_RUN_OUTPUT_LOGS];

/**
 * `--log-order` values accepted by `turbo run`.
 */
export const TURBO_RUN_LOG_ORDER = {
  Auto: "auto",
  Stream: "stream",
  Grouped: "grouped",
} as const;

/**
 * Type for {@link TURBO_RUN_LOG_ORDER} values.
 */
export type TurboRunLogOrder =
  (typeof TURBO_RUN_LOG_ORDER)[keyof typeof TURBO_RUN_LOG_ORDER];

/**
 * `--log-prefix` values accepted by `turbo run`.
 */
export const TURBO_RUN_LOG_PREFIX = {
  Auto: "auto",
  None: "none",
  Task: "task",
} as const;

/**
 * Type for {@link TURBO_RUN_LOG_PREFIX} values.
 */
export type TurboRunLogPrefix =
  (typeof TURBO_RUN_LOG_PREFIX)[keyof typeof TURBO_RUN_LOG_PREFIX];

/**
 * `--dry-run` values accepted by `turbo run`. Use `additionalArgs: ["--dry-run"]`
 * for the bare flag form.
 */
export const TURBO_RUN_DRY_RUN = {
  Text: "text",
  Json: "json",
} as const;

/**
 * Type for {@link TURBO_RUN_DRY_RUN} values.
 */
export type TurboRunDryRun =
  (typeof TURBO_RUN_DRY_RUN)[keyof typeof TURBO_RUN_DRY_RUN];
